import React, { useMemo, useState } from "react";
import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import LOOutcomeModalCst from "@components/LOOutcomeModalCst";
import PathWizard from "../Path/PathWizard";
import { usePathDetailV2 } from "../Path/hooks/usePathDetailV2";
import { useEditPathV2 } from "../Path/hooks/useEditPathV2";
import { useSubmitPathV2 } from "../Path/hooks/useSubmitPathV2";
import { PathWizardData } from "../Path/types";
import { useSaveDraft } from "../hooks/useSaveDraft";
import { PERMISSIONS, usePermissions } from "@utils/permissions";
import { usePromoteDraft } from "../hooks/usePromoteDraft";
import {
  DraftErrorInfo,
  extractPromoteError,
} from "../hooks/parsePromoteErrors";
import DraftPromoteErrorDialog from "../components/DraftPromoteErrorDialog";
import { flattenSelectedLOItems } from "../utils/loStructure";

interface EditPathProps {
  duplication?: boolean;
}

const EditPath: React.FC<EditPathProps> = ({ duplication = false }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation("wizard-path");
  const { id: corporateId = "", idPath = "" } = useParams();
  const catalogUrl = `/v2/corporate/${corporateId}/detail/catalog`;

  const [successOpen, setSuccessOpen] = useState(false);
  const { can } = usePermissions();
  const readOnly = !duplication && !can(PERMISSIONS.catalog.write);
  const [isPublishingDraft, setIsPublishingDraft] = useState(false);
  const [promoteError, setPromoteError] = useState<DraftErrorInfo | null>(null);

  const {
    wizardData,
    initialInitiativeIds,
    initialEditorial,
    isLoading,
    error,
    mutate,
  } = usePathDetailV2({ corporateId, idPath });

  const { submit: submitEdit, isSubmitting: isSubmittingEdit } = useEditPathV2({
    corporateId,
    loId: Number(idPath),
    initialInitiativeIds,
    initialEditorial,
  });

  const { submit: submitAdd, isSubmitting: isSubmittingAdd } = useSubmitPathV2({
    corporateId,
  });

  const { saveDraft, isSavingDraft } = useSaveDraft({
    corporateId,
    type: "path",
  });
  const { promoteDraft } = usePromoteDraft({ corporateId });

  const isSubmitting = duplication
    ? isSubmittingAdd || isPublishingDraft
    : isSubmittingEdit;

  const initialData = useMemo<PathWizardData | null>(() => {
    if (!wizardData) return null;
    if (!duplication) return wizardData;
    return {
      ...wizardData,
      title: `${wizardData.title || ""} - Copied`,
      schedule_publication: false,
      publish_date: null,
      schedule_obsolescence: false,
      obsolescent_date: null,
      selected_los: wizardData.selected_los.map((lo) =>
        lo.kind === "section"
          ? {
              ...lo,
              locked: false,
              items: lo.items.map((item) => ({ ...item, locked: false })),
            }
          : { ...lo, locked: false },
      ),
      useful_links: wizardData.useful_links?.map((link) => ({
        ...link,
        id: null,
      })),
    };
  }, [wizardData, duplication]);

  const hasDraftChildren = (data: PathWizardData) =>
    flattenSelectedLOItems(data.selected_los).some((lo) => lo.entity?.is_draft);

  const publishWithDraftChildren = async (data: PathWizardData) => {
    setIsPublishingDraft(true);
    try {
      const draft = await saveDraft(data);
      try {
        await promoteDraft(draft.id);
      } catch (err) {
        setPromoteError(extractPromoteError(err));
        throw err;
      }
    } finally {
      setIsPublishingDraft(false);
    }
  };

  const handleComplete = async (data: PathWizardData) => {
    try {
      if (duplication) {
        if (hasDraftChildren(data)) {
          await publishWithDraftChildren(data);
        } else {
          await submitAdd(data);
        }
      } else {
        await submitEdit(data);
      }
      setSuccessOpen(true);
    } catch (err) {
      console.error(
        duplication
          ? "[path-wizard] duplicate failed"
          : "[path-wizard] edit failed",
        err,
      );
      throw err;
    }
  };

  const handleSaveDraft = async (data: PathWizardData) => {
    try {
      await saveDraft(data);
      navigate(`${catalogUrl}?tab=all`);
    } catch (err) {
      console.error("[path-wizard] saveDraft failed", err);
      throw err;
    }
  };

  const handleDismissSuccess = () => {
    if (!duplication) void mutate();
    navigate(catalogUrl);
  };

  if (isLoading || (!initialData && !error)) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !initialData) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          gap: 1,
          color: theme.palette.text.primary,
        }}
      >
        <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>
          {t("edit-load-error.title") || "Impossibile caricare il percorso"}
        </Typography>
        <Typography sx={{ fontSize: "13px" }}>
          {error || t("edit-load-error.description") || ""}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <PathWizard
        initialData={initialData}
        onComplete={handleComplete}
        onSaveDraft={duplication ? handleSaveDraft : undefined}
        isSavingDraft={isSavingDraft}
        isSubmitting={isSubmitting}
        mode={duplication ? "add" : "edit"}
        readOnly={readOnly}
        duplication={duplication}
      />
      <LOOutcomeModalCst
        open={successOpen}
        entityType="path"
        mode={duplication ? "add" : "edit"}
        onDismiss={handleDismissSuccess}
      />
      <DraftPromoteErrorDialog
        error={promoteError}
        onClose={() => setPromoteError(null)}
      />
    </>
  );
};

export default EditPath;
